import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Card, CardBody, Col, Row } from "reactstrap";

import FilterSliderModal from "../../../CommonElements/FilterSliderModal";
import SearchBar from "../../../CommonElements/SearchBar";
import YearNavigator from "../../../CommonElements/YearNavigator";
import LearningPlanCard from "../../../container/LearningPlanCard";
import NoDataFound from "../../../container/NoDataFound";
import { useAppDispatch, useAppSelector } from "../../../ReduxToolkit/Hooks";
import { getLearningPlanByUserId } from "../../../ReduxToolkit/Reducers/LearningPlanMaster";
import { getJobRoleList } from "../../../ReduxToolkit/Reducers/ManageMasterDataSlice";
import {
  getDomainList,
  getFilterRolesList,
  getProficiencyList,
} from "../../../ReduxToolkit/Reducers/MasterSlice";
import { Domain, JobRole, Level, Select } from "../../../utils/Constant";
import {
  formatCustomDate,
  formatMinutes,
  getOngoingFiscalYear,
} from "../../../utils/helper/helper";

const LearningGoals = ({ userId }) => {
  const dispatch = useAppDispatch();
  const [clearData, setClearData] = useState(false);
  const [fyYear, setFyYear] = useState(getOngoingFiscalYear());
  const [selectedFilterValues, setSelectedFilterValues] = useState({});
  const [defaultFilterValues, setDefaultFilterValues] = useState({});
  const { learningPlanByUserId } = useAppSelector(
    state => state.learningPlanMaster
  );
  const { domainList, roleOnFilter, proficiencyList } = useAppSelector(
    state => state.master
  );
  const [roleFilterList, setRoleFilterList] = useState([]);
  const [domainFilterList, setDomainFilterList] = useState([]);
  const [proficiencyListData, setProficiencyListData] = useState(null);
  const [data, setData] = useState([]);
  const [searchData, setSearchData] = useState("");

  useEffect(() => {
    dispatch(getDomainList());
    dispatch(getFilterRolesList());
    dispatch(getProficiencyList());
    dispatch(getJobRoleList());
  }, [dispatch]);

  const handleYearChange = (year: string) => {
    setSearchData("");
    setSelectedFilterValues({});
    setDefaultFilterValues({});
    setClearData(true);
    setFyYear(year);
  };

  useEffect(() => {
    if (clearData) {
      setClearData(false);
    }
  }, [clearData]);

  useEffect(() => {
    const value = [];
    if (domainList) {
      domainList?.domain?.forEach(item => {
        value.push({
          name: item.id.toString(),
          value: item.domain_name,
          label: item.domain_name,
        });
      });
    }
    setDomainFilterList(value);
  }, [domainList]);

  useEffect(() => {
    const levels = [];
    proficiencyList?.proficiency?.forEach(sites => {
      const userObj = {
        label: sites?.level_name,
        value: sites?.level_name,
        name: sites?.id.toString(),
      };
      levels.push(userObj);
    });
    setProficiencyListData(levels);
  }, [proficiencyList]);

  useEffect(() => {
    const value = [];
    if (roleOnFilter) {
      roleOnFilter?.job_role?.forEach(item => {
        value.push({
          name: item?.id,
          label: item?.name,
          value: item?.name,
        });
      });
    }
    setRoleFilterList(value);
  }, [roleOnFilter]);

  useEffect(() => {
    dispatch(getLearningPlanByUserId({ id: userId, year: fyYear }));
  }, [dispatch, userId, fyYear]);

  useEffect(() => {
    if (learningPlanByUserId?.learning_plans) {
      if (!searchData) {
        setData(learningPlanByUserId?.learning_plans || []);
        return;
      }

      const filtered = learningPlanByUserId?.learning_plans?.filter(item =>
        item?.name?.toLowerCase().includes(searchData?.toLowerCase())
      );

      setData(filtered || []);
    }
  }, [learningPlanByUserId?.learning_plans, searchData]);

  const isPastDate = (dateString: string): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const givenDate = new Date(dateString);
    givenDate.setHours(0, 0, 0, 0);

    return today > givenDate;
  };

  const handleDone = async (values: { [key: string]: string[] }) => {
    setClearData(true);
    setSearchData("");
    if (values["domain"] || values["role"] || values["level"]) {
      dispatch(
        getLearningPlanByUserId({
          id: userId,
          year: fyYear,
          domain: values["domain"]?.toString() || "",
          role: values["role"]?.toString() || "",
          level: values["level"]?.toString() || "",
        })
      );
    }
  };

  const handleClear = async () => {
    setClearData(true);
    setSearchData("");
    dispatch(getLearningPlanByUserId({ id: userId, year: fyYear }));

    setSelectedFilterValues({});
    setDefaultFilterValues({});
  };

  const handleSearch = searchTerm => {
    setSearchData(searchTerm);
  };

  return (
    <div>
      <Card className="p-1 mb-3">
        <CardBody>
          <Row className="align-items-center gap-lg-0 gap-3">
            <Col
              xs="12"
              lg="4"
              className="d-flex justify-content-start gap-3"
            ></Col>
            <Col
              xs="12"
              lg="8"
              className="d-flex flex-column flex-lg-row justify-content-start justify-content-lg-end gap-3 align-items-center"
            >
              <Row className="justify-content-center align-items-center">
                <Col
                  lg="6"
                  md="6"
                  xs="12"
                  className="d-flex gap-3 align-items-center"
                >
                  <SearchBar
                    showSearch
                    onSearch={handleSearch}
                    clearOnProp={clearData}
                  />
                  <div className="mt-1">
                    <FilterSliderModal
                      dropdowns={[
                        {
                          label: Domain,
                          key: "domain",
                          tooltipText: `${Select} ${Domain}`,
                          options: domainFilterList,
                          isMultiSelect: true,
                        },
                        {
                          label: JobRole,
                          key: "role",
                          tooltipText: `${Select} ${JobRole}`,
                          options: roleFilterList,
                          isMultiSelect: true,
                        },
                        {
                          label: Level,
                          key: "level",
                          tooltipText: `${Select} ${Level}`,
                          options: proficiencyListData,
                          isMultiSelect: true,
                        },
                      ]}
                      selectedFilterValues={selectedFilterValues}
                      defaultFilterValues={defaultFilterValues}
                      setSelectedFilterValues={setSelectedFilterValues}
                      setDefaultFilterValues={setDefaultFilterValues}
                      onDone={handleDone}
                      onClear={handleClear}
                    />
                  </div>
                </Col>
                <Col lg={"6"} md="5" xs="8">
                  <div>
                    <YearNavigator
                      pastYears={20}
                      futureYears={20}
                      onYearChange={handleYearChange}
                    />
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </CardBody>
      </Card>
      <Card>
        <CardBody>
          <div>
            {data?.length > 0 ? (
              <div className="learning-plan-grid">
                {data?.map(item => {
                  return (
                    <LearningPlanCard
                      key={item.id}
                      id={item.id}
                      planType={item?.level}
                      title={item?.name}
                      creator={[]}
                      showDrop={false}
                      totalDuration={formatMinutes(item?.duration || 0)}
                      learnersCount={item?.users}
                      groupsCount={item?.user_groups}
                      thumbnailUrl={item?.thumbnail_url}
                      isArchived={false}
                      courses={item?.course_count}
                      assessments={item?.assessment_count}
                      score={item?.progress}
                      userManagement
                      userId={userId}
                      learningPlanDate={formatCustomDate(item?.target_date)}
                      overdue={isPastDate(item?.target_date)}
                    />
                  );
                })}
              </div>
            ) : (
              <NoDataFound />
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

LearningGoals.propTypes = {
  userId: PropTypes.number,
};

export default LearningGoals;
